/**
 * PolicyEditor.tsx — Alert routing policy inline editor
 *
 * Purpose:    Form for creating or editing an alert routing policy. Manages
 *             condition builder (field/operator/value rows) and escalation
 *             chain steps.
 * Inputs:     initial?: Partial<AlertPolicy>, onSave, onCancel
 * Outputs:    Inline form that calls onSave on submit
 * Constraints: Inline (no modal); cancel must not lose changes in parent list
 */
import { useState } from 'react'
import type {
  AlertPolicy,
  AlertActionType,
  AlertCondition,
  EscalationStep,
} from '../../hooks/useAlertRouterData'

const DEFAULT_POLICY: Omit<AlertPolicy, 'id' | 'createdAt'> = {
  name: '',
  enabled: true,
  conditions: [{ field: 'severity', operator: 'eq', value: 'p1' }],
  escalationChain: [{ delayMinutes: 0, actionType: 'email', target: '' }],
}

export function PolicyEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<AlertPolicy>
  onSave: (p: Omit<AlertPolicy, 'id' | 'createdAt'> & { id?: string }) => Promise<boolean>
  onCancel: () => void
}) {
  const [form, setForm] = useState({ ...DEFAULT_POLICY, ...initial })
  const [saving, setSaving] = useState(false)

  const setCondition = (i: number, patch: Partial<AlertCondition>) => {
    setForm((f) => ({
      ...f,
      conditions: f.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }))
  }

  const setEscalation = (i: number, patch: Partial<EscalationStep>) => {
    setForm((f) => ({
      ...f,
      escalationChain: f.escalationChain.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    await onSave({ ...form, id: initial?.id })
    setSaving(false)
    onCancel()
  }

  return (
    <div className="rounded-lg border border-sky-200 dark:border-sky-800/50 bg-brand-surface/80 p-4">
      <div className="mb-3 flex items-center gap-3">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Policy name"
          className="flex-1 rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
        />
        <label className="flex items-center gap-2 text-sm text-brand-secondary">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            className="rounded"
          />
          Enabled
        </label>
      </div>

      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
        Conditions
      </h4>
      {form.conditions.map((cond, i) => (
        <div key={i} className="mb-2 flex flex-wrap gap-2">
          <select
            value={cond.field}
            onChange={(e) => setCondition(i, { field: e.target.value as AlertCondition['field'] })}
            className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
          >
            <option value="severity">severity</option>
            <option value="source">source</option>
            <option value="tag">tag</option>
          </select>
          <select
            value={cond.operator}
            onChange={(e) => setCondition(i, { operator: e.target.value as AlertCondition['operator'] })}
            className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
          >
            <option value="eq">= (equals)</option>
            <option value="neq">≠ (not equals)</option>
            <option value="contains">contains</option>
          </select>
          <input
            type="text"
            value={cond.value}
            onChange={(e) => setCondition(i, { value: e.target.value })}
            placeholder="value"
            className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          setForm((f) => ({
            ...f,
            conditions: [...f.conditions, { field: 'tag', operator: 'eq', value: '' }],
          }))
        }
        className="mb-4 text-xs text-sky-700 dark:text-sky-400 hover:underline"
      >
        + Add condition
      </button>

      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
        Escalation chain
      </h4>
      {form.escalationChain.map((step, i) => (
        <div key={i} className="mb-2 flex flex-wrap gap-2">
          <input
            type="number"
            value={step.delayMinutes}
            min={0}
            onChange={(e) => setEscalation(i, { delayMinutes: Number(e.target.value) })}
            className="w-20 rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
            title="Delay minutes"
          />
          <span className="self-center text-xs text-brand-muted">min delay</span>
          <select
            value={step.actionType}
            onChange={(e) => setEscalation(i, { actionType: e.target.value as AlertActionType })}
            className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
          >
            <option value="email">Email</option>
            <option value="slack">Slack</option>
            <option value="webhook">Webhook</option>
            <option value="pagerduty">PagerDuty</option>
          </select>
          <input
            type="text"
            value={step.target}
            onChange={(e) => setEscalation(i, { target: e.target.value })}
            placeholder="email / channel / URL"
            className="flex-1 rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-xs text-brand-ink"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          setForm((f) => ({
            ...f,
            escalationChain: [
              ...f.escalationChain,
              { delayMinutes: 5, actionType: 'email', target: '' },
            ],
          }))
        }
        className="mb-4 text-xs text-sky-700 dark:text-sky-400 hover:underline"
      >
        + Add step
      </button>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => { void handleSave() }}
          className="rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save policy'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-brand-card px-4 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
